/* Router.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const UrlUtils = require("url");
const _ = require("lodash");
const SteveUtils = require("@gluenetworks/steve_core_utils");
const minimatch = require("minimatch");

/**
 * @typedef RouteDetails
 * @type {Object.<string,string>}
 * @description An object that represents the information associated with the route. Each of the properties in this
 * object represents a route token without the underscores found in the uri. There is one special property named
 * rd this represents the Route Data associated with a route, all other properties represent Path Tokens.
 * @property {string} [rd=undefined] - The routeData associated with the STEVE Application.
 */
/**
 * Manages the navigation, and is the facade between STEVE and the browser's APIs.
 * @fires RouteDetailsChanged - fired when the route has changed in the browser.
 * @fires RouteChangeError - fired when the route has changed in the browser and is invalid.
 * @fires RouteRefreshed - fired when a refresh has been requested.
 * @fires RouteRefreshError - - fired when a refresh has been requested, and the routeDetails are invalid.
 */
class Router extends EventEmitter {
    constructor(Window, History, logger, opts) {
        super();
        this._logger = logger;
        this._window = Window;
        this._history = History;
        this._encodeRouteDataOption = _.get(opts, "encodeRouteData", true);
        this._rollups = [];
        //Whenever the route changes (back/forward/human typing)
        this._window.addEventListener("hashchange", (evt) => {
            //Grab the current location info from the hash
            let oldURL = _.get(evt, "oldURL", ""),
                newURL = _.get(evt, "newURL", ""),
                oldDetails = null,
                newDetails = null;
            try {
                try {
                    oldDetails = this.parseUrl(oldURL);
                } catch (e) {
                    this._logger.warn({oldURL: oldURL, error: e}, "Failed to parse previous URL");
                }
                newDetails = this.parseUrl(newURL);
                this.onRouteChange(newDetails, oldDetails, {
                    causedBy: "HashChange",
                });
            } catch (parseErr) {
                this._logger.error(
                    {newURL: newURL, error: parseErr},
                    "Failed to parse destination URL"
                );
                this.emit("RouteChangeError", parseErr, {
                    oldHash: evt.oldURL,
                    newHash: evt.newURL,
                });
            }
        });
    }

    /**************************************/
    /* START HANDLER METHODS */

    /**************************************/
    /**
     * Called whenever the route changes,
     * this can be via a hashchange (meaning the user typed into the url bar and changed the hashbang),
     * or one of the navigation methods was called.
     *
     * @param {RouteDetails} newRouteDetails
     * @param {RouteDetails} oldRouteDetails
     * @param {object} sourceDetails - Information about the source of the route change
     * @param {string} causedBy - Tells you what caused the route change, can be either RouteReplaced, RoutePushed, or HashChange.
     */
    onRouteChange(newRouteDetails, oldRouteDetails, sourceDetails) {
        let newPaths = _.cloneDeep(newRouteDetails);
        let oldPaths = _.cloneDeep(oldRouteDetails);
        let newRd = newPaths.rd || {};
        delete newPaths.rd;
        let oldRd = oldPaths.rd || {};
        delete oldPaths.rd;

        //Check Diff and fire specific Events
        let eventNames = _.isFunction(this.eventNames)
            ? this.eventNames()
            : Object.keys(this._events); // eventNames() fails with events@1.1.1 so the _events fallback
        let routeDataDiff = SteveUtils.diff.dotDiff(oldRd, newRd, {flattenResults: true});
        let pathsDiff = SteveUtils.diff.dotDiff(oldPaths, newPaths, {flattenResults: true});

        let rollupsToEmit = {};
        let addChangeToRollup = (rollupName, eventName, data) => {
            if (!Object.hasOwn(rollupsToEmit, rollupName)) {
                rollupsToEmit[rollupName] = {};
            }
            let token = this._getPathTokenNameFromEventName(eventName);
            if (!token) {
                token = this._getRouteDataGlobFromEventName(eventName);
                if (!rollupsToEmit[rollupName].rd) {
                    rollupsToEmit[rollupName].rd = {};
                }
                rollupsToEmit[rollupName].rd[token] = data;
            } else {
                rollupsToEmit[rollupName][token] = data;
            }
        };
        //Test to see if anyone has registered for a path token that has changed
        Object.keys(pathsDiff).forEach((dotNotation) => {
            let dotDiffValue = pathsDiff[dotNotation];
            let eventName = this._getPathTokenEventName(dotNotation);
            if (eventNames.indexOf(eventName) !== -1) {
                let oldPathToken = _.get(oldPaths, dotNotation);
                let newPathToken = _.get(newPaths, dotNotation);
                this.emit(eventName, newPathToken, oldPathToken, {
                    oldPathToken: oldPathToken,
                    newPathToken: newPathToken,
                    diffDetails: dotDiffValue,
                    routeDetails: {
                        newRouteDetails: newRouteDetails,
                        oldRouteDetails: oldRouteDetails,
                        sourceDetails: sourceDetails,
                    },
                });
            }
            this._getMatchingRollupEvents(eventName).forEach((rollupString) => {
                let oldPathToken = _.get(oldPaths, dotNotation);
                let newPathToken = _.get(newPaths, dotNotation);
                addChangeToRollup(rollupString, eventName, {
                    oldPathToken: oldPathToken,
                    newPathToken: newPathToken,
                    diffDetails: dotDiffValue,
                });
            });
        });

        //Test to see if anyone has registered for a piece of the Route Data that has changed
        Object.keys(routeDataDiff).forEach((dotNotation) => {
            let eventName = this._getRouteDataEventName(dotNotation);
            eventNames.forEach((glob) => {
                let pathGlob = glob.replace(/\./g, "/");
                let eventPath = eventName.replace(/\./g, "/");
                if (minimatch(eventPath, pathGlob)) {
                    let or = _.get(oldRd, dotNotation);
                    let nr = _.get(newRd, dotNotation);
                    this.emit(glob, newRd, oldRd, {
                        path: dotNotation,
                        oldRd: or,
                        newRd: nr,
                        diffDetails: routeDataDiff[dotNotation],
                        routeDetails: {
                            newRouteDetails: newRouteDetails,
                            oldRouteDetails: oldRouteDetails,
                            sourceDetails: sourceDetails,
                        },
                    });
                }
            });
            this._globMatchRollupEvents(eventName).forEach((rollupString) => {
                let or = _.get(oldRd, dotNotation);
                let nr = _.get(newRd, dotNotation);
                addChangeToRollup(rollupString, eventName, {
                    path: dotNotation,
                    oldRd: or,
                    newRd: nr,
                    diffDetails: routeDataDiff[dotNotation],
                });
            });
        });

        //find all the rollup events and emit a generic style event if any of the tokens changed
        Object.keys(rollupsToEmit).forEach((eventName) => {
            let changeData = rollupsToEmit[eventName];
            this.emit(eventName, changeData, {
                sourceDetails: sourceDetails,
                oldRouteDetails: oldRouteDetails,
                newRouteDetails: newRouteDetails,
                pathTokenDiff: pathsDiff,
                routeDataDiff: routeDataDiff,
            });
        });

        //Fire the Generic Route Details have changed
        this.emit("RouteDetailsChanged", newRouteDetails, oldRouteDetails, {
            sourceDetails: sourceDetails,
            oldRouteDetails: oldRouteDetails,
            newRouteDetails: newRouteDetails,
            pathTokenDiff: pathsDiff,
            routeDataDiff: routeDataDiff,
        });
    }

    /**************************************/
    /* END HANDLER METHODS */
    /* START NAVIGATION METHODS */

    /**************************************/
    /**
     * Reload the page in the browser, optionally can tell the browser to not use any cached data.
     * @param {boolean} [hard] - If true will reload without using the cache.
     */
    reload(hard) {
        this._window.location.reload(hard);
    }

    /**
     * Refresh the current STEVE route, DO NOT reload the page.
     */
    refresh() {
        //Grab the current location info from the hash
        try {
            let routeDetails = this._getCurrentRouteDetails();
            this.emit("RouteRefreshed", routeDetails);
        } catch (parseErr) {
            this.emit("RouteRefreshError", parseErr, {
                href: this._window.location.href,
            });
        }
    }

    /**
     * Navigate to an external location.
     * @param {string} url - A proper url to navigate to.
     * @param {boolean} [newWindow=false] - True if you want to open a new window.
     */
    navigateAway(url, newWindow) {
        if (newWindow) {
            this._window.open(url);
        } else {
            this._window.location.href = url;
        }
    }

    /**
     * Navigate to an external location, if another STEVE App can optionally pass in routeDetails.
     * @param {string} path - The path to navigate to.
     * @param {RouteDetails} [routeDetails] - Route information overrides.
     * @param {boolean} [newWindow=false] - True if you want to open a new window.
     */
    navigateTo(path, routeDetails, newWindow) {
        let newUrl = this.buildUrl(path, routeDetails);

        if (newWindow) {
            this._window.open(newUrl);
        } else {
            this._window.location.href = newUrl;
        }
    }

    /**
     * Jump to another route within the STEVE Application.
     * @param {RouteDetails} [routeDetails] - Destination route information.
     * @parma {boolean} [newWindow=false] - True if you want to open a new window.
     */
    goTo(routeDetails, newWindow) {
        let currentRouteDetails = this._getCurrentRouteDetails();
        let newRouteDetails = this.combineRouteDetails(currentRouteDetails, routeDetails);
        let hash = this.buildHash(newRouteDetails);
        if (newWindow) {
            this.navigateTo(this._window.location.pathname, routeDetails, newWindow);
        } else {
            //If the hash is the same the HashChange Event will not fire so we have to force a refresh
            if (_.isEqual(currentRouteDetails, newRouteDetails)) {
                this.refresh();
            } else {
                this._window.location.hash = hash;
            }
        }
    }

    /**
     * open page in a help tab
     * @param {RouteDetails} routeDetails
     * @param {string} tabname
     */
    navigateToHelpTab(routeDetails, tabname) {
        // Build hash from route details without combining with current route
        let hash = this.buildHash(routeDetails);

        // Use origin + hash to avoid URL duplication issues
        let newUrl = this._window.location.origin + "/" + hash;

        let childTab = this._window.open(newUrl, tabname);

        // Check if window opened successfully (popup blocker check)
        if (!childTab) {
            console.error("Failed to open help window - popup may be blocked");
            return;
        }

        // Wait for the window to load before accessing its DOM
        if (routeDetails.sln) {
            try {
                childTab.addEventListener("load", function () {
                    try {
                        const iframe = childTab.window.document.body?.querySelector("iframe");
                        if (iframe) {
                            iframe.src = "/help_html/index.htm#t=" + routeDetails.sln;
                        }
                    } catch (err) {
                        console.error("Error setting help iframe source:", err);
                    }
                });
            } catch (err) {
                console.error("Error adding load listener to help window:", err);
            }
        }
    }

    /**************************************/
    /* END NAVIGATION METHODS */
    /* START HISTORY MANIPULATION METHODS */

    /**************************************/
    updateRouteInfo(routeDetails, opts) {
        let combined = this.combineRouteDetails(this._getCurrentRouteDetails(), routeDetails);
        return this.replaceRouteInfo(combined, opts);
    }

    replaceRouteInfo(routeDetails, opts) {
        let oldRouteDetails = this._getCurrentRouteDetails();
        let hash = this.buildHash(routeDetails);
        this._history.replaceState(routeDetails, "", hash);
        this.onRouteChange(routeDetails, oldRouteDetails, {
            causedBy: "RouteReplaced",
            originator: _.get(opts, "originator"),
        });
    }

    pushUpdatedRouteInfoHistory(routeDetails, opts) {
        let combined = this.combineRouteDetails(this._getCurrentRouteDetails(), routeDetails);
        return this.pushRouteInfoHistory(combined, opts);
    }

    pushRouteInfoHistory(routeDetails, opts) {
        let oldRouteDetails = this._getCurrentRouteDetails();
        let hash = this.buildHash(routeDetails);
        this._history.pushState(routeDetails, "", hash);
        this.onRouteChange(routeDetails, oldRouteDetails, {
            causedBy: "RoutePushed",
            originator: _.get(opts, "originator"),
        });
    }

    /**
     * Go Back to a previous set of Route Details.
     * If count is provided will go further back in history than n-1.
     * @param {number} [count] - How far to go back in history, if NOT provided goes back 1.
     */
    goBack(count) {
        if (!count) {
            this._history.back();
        } else {
            this._history.go(-Math.abs(count));
        }
    }

    /**
     * If the user has navigated back in history this allows them to go Forward.
     * If count is provided will go further forward in history.
     * @param {number} [count] - How far to go forward in history, if NOT provided goes forward 1.
     */
    goForward(count) {
        if (!count) {
            this._history.forward();
        } else {
            this._history.go(Math.abs(count));
        }
    }

    /**************************************/
    /* END HISTORY MANIPULATION METHODS */
    /* START ROUTE DETAILS MANIPULATION METHODS */

    /**************************************/
    /**
     * Given the passed in routeData String attempt to convert it into JSON.
     * @param {string} rd - Route Data String
     * @returns {RouteData}
     * @private
     */
    _parseRouteData(rd) {
        let parsedRd;
        let parsePlainJson = (plainRd) => {
            let jsonStr = decodeURI(plainRd);
            return JSON.parse(jsonStr);
        };
        let parseEncodedJson = (encodedJson) => {
            let decodedRdStr = SteveUtils.b64DecodeUnicode(encodedJson);
            return JSON.parse(decodedRdStr);
        };
        let parseError = new Error("Failed to parse route data. Route Data-> " + rd);
        //if the json is supposed to be encoded try and decode it first
        if (this.encodeRouteDataOption) {
            try {
                parsedRd = parseEncodedJson(rd);
            } catch (e) {
                try {
                    parsedRd = parsePlainJson(rd);
                } catch (ee) {
                    throw parseError;
                }
            }
        } else {
            try {
                parsedRd = parsePlainJson(rd);
            } catch (e) {
                try {
                    parsedRd = parseEncodedJson(rd);
                } catch (ee) {
                    throw parseError;
                }
            }
        }
        return parsedRd;
    }

    /**
     * Parse a url String into RouteDetails.
     * @param {String} urlIn - url to be parsed.
     * @returns {RouteDetails} The parsed routeDetails.
     */
    parseUrl(urlIn) {
        let url = UrlUtils.parse(urlIn);
        let path = url.path || "";
        path = path.replace(/\/$/, "");
        let hash = url.hash || "";
        //First parse the hash
        let routeDetails = this.parsePathString(hash);
        //Then check the path for any route tokens (if found they will be used as a fallback).
        //In other words the hash takes precedence
        routeDetails = _.defaults(routeDetails, this.parsePathString(path));
        routeDetails.url = url.href;

        return routeDetails;
    }

    /**
     * Parse the passed in string for route tokens or routeData
     * @param {string} route - A route string, something that looks like a path or url.
     * @returns {RouteDetails} The parsed routeDetails.
     */
    parsePathString(route) {
        if (!route) {
            return {};
        } else if (!_.isString(route)) {
            throw new Error("Cannot parse a Hash that is not a String.");
        } else {
            let routeDetails = {};
            let regex = /(?:^|\/)(_.+?_)\//g;
            let split = route.split(regex);
            split.forEach((entry, idx) => {
                if (entry !== "") {
                    let match = entry.match(/^_(.+)_$/);
                    if (match) {
                        let token = match[1];
                        let value = split[idx + 1];
                        // Normalize extra slashes from malformed URLs (e.g. _s_//Dashboard or _s_/Dashboard//)
                        // Skip rd — base64 legitimately uses '/' as a character
                        if (token !== "rd" && typeof value === "string") {
                            value = value.replace(/^\/+|\/+$/g, "");
                        }
                        //NOTE: last one wins...
                        routeDetails[token] = value;
                    }
                }
            });

            //if routeData is provided
            if (routeDetails.rd) {
                routeDetails.rd = this._parseRouteData(routeDetails.rd);
            }

            return routeDetails;
        }
    }

    /**
     * Combine the provided routeDetails from left to right, do NOT manipulate any of them in the process.
     * @param {RouteDetails} ...routeDetails - Varargs of RouteDetails objects.
     * @returns {RouteDetails} Combined RouteDetails object.
     */
    combineRouteDetails(...routeDetails) {
        //Don't destroy any passed in routeDetails objects
        routeDetails.unshift({});

        let newRouteDetails = _.merge.apply(this, routeDetails);
        return newRouteDetails;
    }

    /**
     * @param {string} path - The path to navigate to.
     * @param {RouteDetails} [routeDetails] - Route information overrides.
     */
    buildUrl(pathIn, routeDetailsIn) {
        let routeDetails = this.combineRouteDetails(this.parseUrl(pathIn), routeDetailsIn);

        let hash = this.buildHash(routeDetails);
        let url = UrlUtils.parse(pathIn);
        url.hash = hash;
        return UrlUtils.format(url);
    }

    /**
     * Build a Hashbang (#!/) from RouteDetails.
     * @param {RouteDetails} [routeDetails] - Route information overrides.
     * @returns {string} Returns the RouteDetails in HashBang
     *      format (#!/app/appName/s/sectionName/sr/subRoute/tkn/token/rd/encodedRouteData
     */
    buildHash(routeDetails) {
        let hash = "#!";
        if (_.isObject(routeDetails)) {
            // Ordered list of route tokens for each section -- if section not found, all route tokens are expressed in hashbang
            const sectionToRouteTokenMap = {
                Dashboard: ["app", "s", "rd", "ra"],
                Settings: ["app", "s", "sln", "rd", "sr"],
            };
            const routeTokens = _.get(
                sectionToRouteTokenMap,
                routeDetails.s,
                Object.keys(routeDetails)
            );

            // List of rd details for each section -- if section not found, all non-null keys are passed in the base64 rd
            const sectionToRdKeyMap = {
                Dashboard: ["triggeredBy", "processingHook"],
            };
            const rdKeys = _.get(
                sectionToRdKeyMap,
                routeDetails.s,
                Object.keys(routeDetails.rd || {})
            );

            routeTokens.forEach((routeToken) => {
                if (routeToken === "rd") {
                    let cleanedRd = {};
                    rdKeys.forEach((rdkey) => {
                        if (routeDetails.rd && routeDetails.rd[rdkey] !== null) {
                            cleanedRd[rdkey] = routeDetails.rd[rdkey];
                        }
                    });
                    let rdStr = JSON.stringify(cleanedRd);
                    if (this.encodeRouteDataOption) {
                        rdStr = SteveUtils.b64EncodeUnicode(rdStr);
                    }
                    hash += `/_rd_/${rdStr}`;
                } else if (routeToken !== "url") {
                    if (!_.isEmpty(routeDetails[routeToken])) {
                        hash += `/_${routeToken}_/${routeDetails[routeToken]}`;
                    } else if (routeDetails.s === "Dashboard" && routeToken === "ra") {
                        hash += "/_ra_/dashboard";
                    }
                }
            });
        }
        return hash;
    }

    /********************************************/
    /* END ROUTE DETAILS MANIPULATION METHODS   */
    /* START CONVENIENT LISTENER METHODS */

    /********************************************/
    _getRollupEventName(pathTokens, routeDataGlobs) {
        let PTEvents = pathTokens.map(this._getPathTokenEventName);
        let RDEvents = routeDataGlobs.map(this._getRouteDataEventName);
        return this._rollupEventNames(PTEvents.concat(RDEvents));
    }

    _getPathTokenRollupEventName(pathTokenArray) {
        let eventNames = pathTokenArray.map(this._getPathTokenEventName);
        return this._rollupEventNames(eventNames);
    }

    _getRouteDataRollupEventName(globArray) {
        let eventNames = globArray.map(this._getRouteDataEventName);
        return this._rollupEventNames(eventNames);
    }

    _getPathTokenNamesFromRollupEventName(name) {
        let events = this._getEventNamesFromRollup(name);
        let PTEvents = events
            .map(this._getPathTokenNameFromEventName)
            .filter((val) => val !== undefined);
        return PTEvents;
    }

    _rollupEventNames(names) {
        return names.join("||");
    }

    _getEventNamesFromRollup(rollupString) {
        return rollupString.split("||");
    }

    _getMatchingRollupEvents(eventName) {
        return this.rollups.filter((rollupString) => {
            return rollupString.indexOf(eventName) > -1;
        });
    }

    _globMatchRollupEvents(eventName) {
        let sourceEventPath = eventName.replace(/\./g, "/");
        return this.rollups.filter((rollupString) => {
            let allEvents = this._getEventNamesFromRollup(rollupString);
            let matched = false;
            _.some(allEvents, (rollupEvt) => {
                let rollupPath = rollupEvt.replace(/\./g, "/");
                if (minimatch(sourceEventPath, rollupPath)) {
                    matched = true;
                    return true;
                }
            });
            return matched;
        });
    }

    _getPathTokenEventName(tokenName) {
        return `_PT_${tokenName}`;
    }

    _getPathTokenNameFromEventName(eventName) {
        return eventName.split("_PT_")[1];
    }

    onPathTokenChange(tokenName, hdlr) {
        return this.on(this._getPathTokenEventName(tokenName), hdlr);
    }

    oncePathTokenChange(tokenName, hdlr) {
        return this.once(this._getPathTokenEventName(tokenName), hdlr);
    }

    removePathTokenChangeListener(tokenName, hdlr) {
        return this.removeListener(this._getPathTokenEventName(tokenName), hdlr);
    }

    removeAllPathTokenDataChangeListeners(tokenName) {
        return this.removeAllListeners(this._getPathTokenEventName(tokenName));
    }

    onPathTokenRollupChange(tokenNameArray, hdlr) {
        let rollupName = this._getPathTokenRollupEventName(tokenNameArray);
        if (!_.includes(this.rollups, rollupName)) {
            this.rollups.push(rollupName);
        }
        return this.on(rollupName, hdlr);
    }

    removePathTokenRollupChangeListener(tokenNameArray, hdlr) {
        let rollupName = this._getPathTokenRollupEventName(tokenNameArray);
        this.removeListener(rollupName, hdlr);
        if (!Object.hasOwn(this._events, rollupName)) {
            this.rollups.pull(rollupName);
        }
    }

    _getRouteDataEventName(glob) {
        return `_RD_${glob}`;
    }

    _getRouteDataGlobFromEventName(eventName) {
        return eventName.split("_RD_")[1];
    }

    onRouteDataChange(glob, hdlr) {
        return this.on(this._getRouteDataEventName(glob), hdlr);
    }

    onceRouteDataChange(glob, hdlr) {
        return this.once(this._getRouteDataEventName(glob), hdlr);
    }

    onRouteDataRollupChange(globArray, hdlr) {
        let rollupName = this._getRouteDataRollupEventName(globArray);
        if (!_.includes(this.rollups, rollupName)) {
            this.rollups.push(rollupName);
        }
        return this.on(rollupName, hdlr);
    }

    removeRouteDataRollupChangeListener(globArray, hdlr) {
        let rollupName = this._getRouteDataRollupEventName(globArray);
        this.removeListener(rollupName, hdlr);
        if (!Object.hasOwn(this._events, rollupName)) {
            this.rollups.pull(rollupName);
        }
    }

    removeRouteDataChangeListener(glob, hdlr) {
        return this.removeListener(this._getRouteDataEventName(glob), hdlr);
    }

    removeAllRouteDataChangeListeners(glob) {
        return this.removeAllListeners(this._getRouteDataEventName(glob));
    }

    onRollupChange(pathTokens, routeDataGlobs, hdlr) {
        let rollupName = this._getRollupEventName(pathTokens, routeDataGlobs);
        if (!_.includes(this.rollups, rollupName)) {
            this.rollups.push(rollupName);
        }
        return this.on(rollupName, hdlr);
    }

    removeRollupChangeListener(pathTokens, routeDataGlobs, hdlr) {
        let rollupName = this._getRollupEventName(pathTokens, routeDataGlobs);
        this.removeListener(rollupName, hdlr);
        if (!Object.hasOwn(this._events, rollupName)) {
            this.rollups.pull(rollupName);
        }
    }

    /**************************************/
    /* END CONVENIENT LISTENER METHODS */
    /* START ROUTE DETAILS METHODS */

    /********************************************/
    /**
     * Retrieves the current RouteDetails from the Browser.
     * @returns {RouteDetails} the current RouteDetails.
     */
    _getCurrentRouteDetails() {
        return this.parseUrl(this._window.location.href);
    }

    get routeDetails() {
        return this.getRouteDetails();
    }

    getRouteDetails() {
        return this._getCurrentRouteDetails();
    }

    get routeData() {
        return this.getRouteData();
    }

    getRouteData() {
        return _.get(this._getCurrentRouteDetails(), "rd");
    }

    get encodeRouteDataOption() {
        return this.getEncodeRouteDataOption();
    }

    getEncodeRouteDataOption() {
        return this._encodeRouteDataOption;
    }

    set encodeRouteDataOption(ero) {
        this.setEncodeRouteDataOption(ero);
    }

    setEncodeRouteDataOption(ero) {
        this._encodeRouteDataOption = ero;
        return this;
    }

    get origin() {
        return this._window.location.origin;
    }

    get protocol() {
        return this._window.location.protocol;
    }

    get hostname() {
        return this._window.location.hostname;
    }

    get port() {
        return this._window.location.port;
    }

    get pathname() {
        return this._window.location.pathname;
    }

    get rollups() {
        return this.getRollups();
    }

    set rollups(array) {
        this.setRollups(array);
    }

    getRollups() {
        return this._rollups;
    }

    setRollups(array) {
        this._rollups = array;
    }

    /**************************************/
    /* END ROUTE DETAILS METHODS */
    /**************************************/
}

module.exports = Router;
